import { FunctionsHttpError } from '@supabase/supabase-js'
import { z } from 'zod'

import { type AppSupabaseClient } from '@/platform/supabase/client'
import { callRpc } from '@/platform/supabase/rpc'

/**
 * Read Aloud client (FR-72–FR-78): the `assess-reading` Edge Function and the
 * learner RPCs of migration 0012, always with the D-16 learner session token.
 * No Azure credential is ever in the browser (SEC-A); the server takes the
 * learner, family and sentence text from the session and its catalogue.
 *
 * Retry rules are the KT1 trial client's (FR-78): the same attempt id on every
 * submission; Azure rate limiting retried at most twice and "in progress"
 * polled, all within 15 s; timeouts and provider errors are not retried
 * automatically (the learner can press "Thử lại").
 */

export const READ_ALOUD_FUNCTION = 'assess-reading'
export const SESSION_HEADER = 'x-learner-session'
export const RATE_LIMIT_RETRIES = 2
export const ASSESS_WINDOW_MS = 15_000
const INVOKE_TIMEOUT_MS = 30_000

export const readingSchema = z.object({
  client_attempt_id: z.string(),
  passage_id: z.string(),
  sentence_index: z.number().int(),
  assessment_status: z.enum([
    'pending',
    'assessed',
    'no_speech',
    'failed_retryable',
    'failed',
  ]),
  audio_quality: z.enum(['ok', 'low']).nullable(),
  word_flags: z
    .array(z.object({ word: z.string(), reason: z.literal('low_accuracy') }))
    .max(5),
  created_at: z.string().optional(),
  assessed_at: z.string().nullable().optional(),
})
export type Reading = z.infer<typeof readingSchema>

const statusSchema = z.object({
  learner_session: z.boolean(),
  available: z.boolean(),
  consent: z.boolean(),
})
export type ReadAloudStatus = z.infer<typeof statusSchema>

const responseSchema = z.object({
  ok: z.boolean(),
  error: z.string().optional(),
  retryable: z.boolean().optional(),
  reading: readingSchema.optional(),
})

/** Why a submission was refused before anything was assessed. */
export type Refusal =
  | 'learner_session_invalid'
  | 'not_available'
  | 'consent_required'
  | 'invalid_audio'
  | 'too_large'
  | 'not_authenticated'
  | 'bad_request'
  | 'conflict'

const REFUSALS: ReadonlySet<string> = new Set<Refusal>([
  'learner_session_invalid',
  'not_available',
  'consent_required',
  'invalid_audio',
  'too_large',
  'not_authenticated',
  'bad_request',
  'conflict',
])

export type SubmitOutcome =
  /** Assessed or no speech: the result to show (and kept in history). */
  | { readonly kind: 'result'; readonly reading: Reading }
  /** Saved as practice but not assessed (Azure busy/down, daily limit, still checking). */
  | { readonly kind: 'not_assessed'; readonly retryable: boolean }
  | { readonly kind: 'refused'; readonly why: Refusal }
  /** No answer from the server (offline, timeout). Safe to retry. */
  | { readonly kind: 'network' }

export interface SubmitInput {
  readonly clientAttemptId: string
  readonly passageId: string
  readonly sentenceIndex: number
  readonly wav: ArrayBuffer
}

/** Read Aloud for one learner session. */
export interface ReadAloudLearner {
  status(): Promise<ReadAloudStatus>
  history(): Promise<Reading[]>
  submit(input: SubmitInput): Promise<SubmitOutcome>
}

export interface ReadAloudApi {
  forSession(sessionToken: string): ReadAloudLearner
}

export interface ReadAloudDeps {
  sleep(ms: number): Promise<void>
  now(): number
}

const realDeps: ReadAloudDeps = {
  sleep: (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms)
    }),
  now: () => Date.now(),
}

interface Answer {
  readonly status: number | null
  readonly retryAfterMs: number
  readonly body: z.infer<typeof responseSchema> | null
}

async function invoke(
  client: AppSupabaseClient,
  sessionToken: string,
  input: SubmitInput,
): Promise<Answer> {
  // No learner id and no sentence text: the server takes both itself.
  const form = new FormData()
  form.set('audio', new Blob([input.wav], { type: 'audio/wav' }), 'reading.wav')
  form.set('client_attempt_id', input.clientAttemptId)
  form.set('passage_id', input.passageId)
  form.set('sentence_index', String(input.sentenceIndex))
  try {
    const res = await client.functions.invoke<unknown>(READ_ALOUD_FUNCTION, {
      method: 'POST',
      body: form,
      headers: { [SESSION_HEADER]: sessionToken },
      timeout: INVOKE_TIMEOUT_MS,
    })
    const error: unknown = res.error
    const response: Response | undefined =
      error instanceof FunctionsHttpError && error.context instanceof Response
        ? error.context
        : res.response
    let raw: unknown = res.data
    if (error !== null) {
      if (!(error instanceof FunctionsHttpError) || response === undefined)
        return { status: null, retryAfterMs: 0, body: null }
      raw = await response.json().catch(() => null)
    }
    const parsed = responseSchema.safeParse(raw)
    return {
      status: response?.status ?? 200,
      retryAfterMs: Number(response?.headers.get('retry-after') ?? '1') * 1000,
      body: parsed.success ? parsed.data : null,
    }
  } catch {
    return { status: null, retryAfterMs: 0, body: null }
  }
}

export function createReadAloudApi(
  client: AppSupabaseClient,
  deps: ReadAloudDeps = realDeps,
): ReadAloudApi {
  return {
    forSession: (sessionToken) => ({
      status: () =>
        callRpc(
          client,
          'get_read_aloud_status',
          { p_session_token: sessionToken },
          statusSchema,
        ),
      history: () =>
        callRpc(
          client,
          'list_my_read_aloud',
          { p_session_token: sessionToken },
          z.array(readingSchema),
        ),
      submit: async (input) => {
        const started = deps.now()
        let rateLimitRetries = 0
        for (;;) {
          const a = await invoke(client, sessionToken, input)
          if (a.status === null || a.body === null) return { kind: 'network' }
          const reading = a.body.reading
          const status = reading?.assessment_status
          if (
            a.body.ok &&
            reading !== undefined &&
            (status === 'assessed' || status === 'no_speech')
          )
            return { kind: 'result', reading }
          const withinWindow = deps.now() - started + a.retryAfterMs <= ASSESS_WINDOW_MS
          if (withinWindow && a.body.ok && status === 'pending') {
            await deps.sleep(a.retryAfterMs)
            continue
          }
          if (
            withinWindow &&
            a.body.error === 'rate_limited' &&
            a.body.retryable === true &&
            rateLimitRetries < RATE_LIMIT_RETRIES
          ) {
            rateLimitRetries++
            await deps.sleep(a.retryAfterMs)
            continue
          }
          const error = a.body.error ?? ''
          if (REFUSALS.has(error)) return { kind: 'refused', why: error as Refusal }
          return {
            kind: 'not_assessed',
            retryable: status === 'pending' || a.body.retryable === true,
          }
        }
      },
    }),
  }
}
